<template>
  <div class="user-page">
    <div
      v-if="!user"
      class="flex justify-center items-center h-40 text-gray-400"
    >
      Loading...
    </div>
    <template v-else>
      <router-link :to="{ name: 'UsersPage' }" class="up-back-link">
        ← Back to users
      </router-link>
      <!-- ── Top header card ────────────────────────────────── -->
      <div class="up-header-card">
        <div class="up-avatar" :style="{ background: avatarColor(user.name) }">
          {{ initials(user.name) }}
        </div>
        <div class="up-header-info">
          <div class="flex items-center gap-3 flex-wrap">
            <h1 class="up-name">{{ user.name }}</h1>
            <span class="role-badge">{{ user.role }}</span>
            <span
              v-if="user.enabled === '1'"
              class="status-badge status-badge--active"
              >Active</span
            >
            <span v-else class="status-badge status-badge--inactive"
              >Inactive</span
            >
            <span
              v-if="user.invalidLoginAttempts >= 8"
              class="status-badge status-badge--locked"
              >Locked</span
            >
          </div>
          <p class="up-email">{{ user.email }}</p>
        </div>
        <div class="up-header-actions">
          <button
            v-if="!editing"
            class="action-btn action-btn--primary"
            @click="editing = true"
          >
            Edit
          </button>
          <button class="action-btn" @click="getResetPasswordLink">
            Reset password link
          </button>
          <button
            v-if="user.invalidLoginAttempts >= 8"
            class="action-btn action-btn--danger"
            @click="resetLoginAttempts"
          >
            Unlock account
          </button>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center py-8">
        <BaseSmallSpinner />
      </div>
      <div v-else class="up-body">
        <!-- ── Profile info ────────────────────────────────── -->
        <div class="up-card">
          <h2 class="up-card-title">Profile</h2>
          <div class="up-field-grid">
            <div class="up-field">
              <div class="up-field-label">Name</div>
              <div v-if="!editing" class="up-field-value">{{ user.name }}</div>
              <input v-else v-model="user.name" class="up-input" />
            </div>
            <div class="up-field">
              <div class="up-field-label">Email</div>
              <div class="up-field-value">{{ user.email }}</div>
            </div>
            <div class="up-field">
              <div class="up-field-label">Role</div>
              <div v-if="!editing" class="up-field-value">{{ user.role }}</div>
              <select v-else v-model="user.role" class="up-select">
                <option
                  v-for="role in roles"
                  :key="role.code"
                  :value="role.code"
                >
                  {{ role.name }}
                </option>
              </select>
            </div>
            <div class="up-field">
              <div class="up-field-label">Status</div>
              <div class="flex items-center gap-3">
                <span
                  v-if="user.enabled === '1'"
                  class="status-badge status-badge--active"
                  >Active</span
                >
                <span v-else class="status-badge status-badge--inactive"
                  >Inactive</span
                >
                <BaseSwitchCustom
                  v-if="editing"
                  :value="user.enabled === '1'"
                  @input="$event ? (user.enabled = '1') : (user.enabled = '0')"
                />
              </div>
            </div>
            <div class="up-field" v-if="user.invalidLoginAttempts">
              <div class="up-field-label">Failed login attempts</div>
              <div
                class="up-field-value"
                :class="{
                  'text-red-500 font-bold': user.invalidLoginAttempts >= 8
                }"
              >
                {{ user.invalidLoginAttempts }}
              </div>
            </div>
          </div>
        </div>

        <!-- ── Stores ──────────────────────────────────────── -->
        <div class="up-card">
          <h2 class="up-card-title">Stores</h2>
          <div v-if="!editing" class="flex flex-wrap gap-2">
            <span
              v-for="store in user.stores"
              :key="store"
              class="store-chip"
              :class="{ 'store-chip--default': store === user.defaultStore }"
            >
              {{ store }}
              <span
                v-if="store === user.defaultStore"
                class="store-chip-default-label"
                >default</span
              >
            </span>
            <span v-if="user.stores.length === 0" class="text-gray-400 text-sm"
              >No stores assigned</span
            >
          </div>
          <div v-else class="stores-checklist">
            <label
              v-for="store in stores"
              :key="store.code"
              class="store-check-row"
            >
              <input
                type="checkbox"
                class="up-checkbox"
                :value="store.code"
                v-model="user.stores"
              />
              <span>{{ store.name }}</span>
              <span
                v-if="store.code === user.defaultStore"
                class="store-chip-default-label"
                >default</span
              >
            </label>
          </div>
        </div>

        <!-- ── Price Levels ────────────────────────────────── -->
        <div class="up-card">
          <h2 class="up-card-title">Price Levels by Store</h2>
          <div v-if="loadingPriceLevels" class="text-gray-400 text-sm py-2">
            Loading…
          </div>
          <div
            v-else-if="user.stores.length === 0"
            class="text-gray-400 text-sm"
          >
            Enable stores first to assign price levels.
          </div>
          <div v-else class="price-levels-wrap">
            <div
              v-for="storeCode in user.stores"
              :key="'pl-' + storeCode"
              class="price-level-store"
            >
              <div class="price-level-store-title">{{ storeCode }}</div>
              <div
                v-if="storeErrors[storeCode]"
                class="text-red-400 text-sm italic"
              >
                Service not available
              </div>
              <template v-else>
                <!-- View mode: show selected as chips -->
                <div v-if="!editing">
                  <div v-if="getSelectedPriceLevelLabels(storeCode).length">
                    <div class="flex flex-wrap gap-2 mt-1">
                      <span
                        v-for="label in getSelectedPriceLevelLabels(storeCode)"
                        :key="label"
                        class="pl-chip"
                        >{{ label }}</span
                      >
                    </div>
                  </div>
                  <div v-else class="text-gray-400 text-sm italic">
                    No price levels assigned
                  </div>
                </div>
                <!-- Edit mode: checkboxes grouped by dept -->
                <div v-else>
                  <div
                    v-for="(levels, deptName) in groupedPriceLevels(storeCode)"
                    :key="storeCode + '-' + deptName"
                    class="pl-dept-group"
                  >
                    <div class="pl-dept-name">{{ deptName }}</div>
                    <label
                      v-for="level in levels"
                      :key="level.departamentoId + ':' + level.grupoPrecioId"
                      class="pl-check-row"
                    >
                      <input
                        type="checkbox"
                        class="up-checkbox"
                        :value="
                          level.departamentoId + ':' + level.grupoPrecioId
                        "
                        :checked="isPriceLevelSelected(storeCode, level)"
                        @change="
                          togglePriceLevel(
                            storeCode,
                            level,
                            $event.target.checked
                          )
                        "
                      />
                      <span
                        >{{ level.descripcion }}
                        <span class="text-gray-400"
                          >({{ level.codigo }})</span
                        ></span
                      >
                    </label>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- ── Infoauto Seller status ──────────────────────── -->
        <div class="up-card">
          <h2 class="up-card-title">
            Infoauto Seller Status
            <span class="up-card-subtitle" v-if="activeStore">
              · {{ activeStore.name }}
            </span>
          </h2>
          <div v-if="!activeStore" class="text-gray-400 text-sm">
            No active store selected.
          </div>
          <div v-else class="seller-status-body">
            <div
              v-if="sellersLoadingByStore[activeStore.code]"
              class="text-gray-400 text-sm"
            >
              Checking…
            </div>
            <div
              v-else-if="sellersErrorByStore[activeStore.code]"
              class="text-red-400 text-sm italic"
            >
              Service not available
            </div>
            <div
              v-else-if="!(sellersByStore[activeStore.code] || []).length"
              class="seller-status-missing"
            >
              <span class="status-badge status-badge--locked">
                ✗ Not registered as Infoauto seller for this store
              </span>
              <div class="text-gray-500 text-xs mt-1">
                Email <b>{{ user.email }}</b> not found in
                <code>infoauto_sellers</code>.
              </div>
            </div>
            <div v-else>
              <div
                v-for="seller in sellersByStore[activeStore.code]"
                :key="'sv-' + seller.id"
                class="seller-status-item"
              >
                <div class="seller-status-info">
                  <span
                    v-if="seller.enabled"
                    class="status-badge status-badge--active"
                    >✓ Enabled</span
                  >
                  <span v-else class="status-badge status-badge--inactive"
                    >Disabled</span
                  >
                  <span class="seller-status-detail">
                    {{ seller.nombreVendedor || "—" }}
                    <span class="text-gray-400"
                      >· {{ seller.nombreDpto || seller.departamentoId }} · ID
                      {{ seller.vendedorId || seller.id }}</span
                    >
                  </span>
                </div>
                <BaseSwitchCustom
                  :value="!!seller.enabled"
                  @input="toggleSeller(seller)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ── Permissions ─────────────────────────────────── -->
        <div class="up-card">
          <h2 class="up-card-title">Permissions</h2>
          <div class="permissions-grid">
            <div
              v-for="(group, groupName) in groupedPermissions"
              :key="groupName"
              class="perm-group"
            >
              <div class="perm-group-title">{{ groupName }}</div>
              <label
                v-for="permission in group"
                :key="permission.code"
                class="perm-row"
                :class="{ 'perm-row--disabled': !editing }"
              >
                <input
                  type="checkbox"
                  class="up-checkbox"
                  :value="permission.code"
                  v-model="user.access"
                  :disabled="!editing"
                />
                <span>{{ permission.name }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- ── Save / Cancel ───────────────────────────────── -->
        <div v-if="editing" class="up-save-bar">
          <button class="action-btn action-btn--primary" @click="saveChanges">
            Save changes
          </button>
          <button class="action-btn" @click="discardEdition">Cancel</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  getAdminUser,
  getPermissions,
  updateAdminUser,
  getResetPasswordLink as getResetPasswordLinkApi,
  resetLoginAttempts as resetLoginAttemptsApi,
  getPriceLevelsForStore
} from "@/api/masterAdmin/AdminUsersService.js";
import {
  getInfoautoSellersByEmail,
  updateInfoautoSeller
} from "@/api/masterAdmin/InfoautoSellersService.js";

const AVATAR_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f59e0b",
  "#3b82f6",
  "#10b981",
  "#f97316"
];

export default {
  name: "UserPage",
  data() {
    return {
      user: null,
      loading: true,
      permissions: [],
      editing: false,
      originalUser: null,
      priceLevelsByStore: {},
      storeErrors: {},
      loadingPriceLevels: false,
      sellersByStore: {},
      sellersLoadingByStore: {},
      sellersErrorByStore: {}
    };
  },
  async mounted() {
    this.loadUser();
    this.getPermissions();
  },
  computed: {
    stores() {
      return this.$store.getters["general/getStores"];
    },
    activeStore() {
      return this.$store.getters["general/getActiveStore"];
    },
    userIsEdited() {
      return JSON.stringify(this.user) !== JSON.stringify(this.originalUser);
    },
    roles() {
      return this.permissions.filter(p => p.group === "role");
    },
    groupedPermissions() {
      return this.permissions.reduce((groups, p) => {
        if (p.group === "role") return groups;
        if (!groups[p.groupName]) groups[p.groupName] = [];
        groups[p.groupName].push(p);
        return groups;
      }, {});
    }
  },
  watch: {
    "user.stores": {
      handler(newStores) {
        if (!newStores || !this.editing) return;
        const allStores = this.$store.getters["general/getStores"];
        newStores.forEach(storeCode => {
          this.fetchPriceLevels(storeCode, allStores);
          if (!this.user.storePriceLevels[storeCode]) {
            this.$set(this.user.storePriceLevels, storeCode, []);
          }
        });
      },
      deep: true
    }
  },
  methods: {
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map(w => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarColor(name) {
      if (!name) return AVATAR_COLORS[0];
      return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
    },
    discardEdition() {
      this.user = JSON.parse(JSON.stringify(this.originalUser));
      this.editing = false;
    },
    loadUser() {
      this.loading = true;
      getAdminUser(this.$route.params.userId)
        .then(response => {
          let storePriceLevels =
            response.storePriceLevels || response.store_price_levels || {};
          if (response.stores && Array.isArray(response.stores)) {
            const normalized = {};
            response.stores.forEach(storeCode => {
              const camelKey = storeCode.replace(/_([a-z])/g, g =>
                g[1].toUpperCase()
              );
              const value =
                storePriceLevels[storeCode] || storePriceLevels[camelKey] || [];
              normalized[storeCode] = Array.isArray(value)
                ? value
                : value
                ? [value]
                : [];
            });
            response.storePriceLevels = normalized;
          } else {
            response.storePriceLevels = storePriceLevels;
          }
          this.user = response;
          this.originalUser = JSON.parse(JSON.stringify(response));
          this.loadPriceLevelsForStores();
        })
        .catch(console.error)
        .finally(() => {
          this.loading = false;
        });
    },
    async loadPriceLevelsForStores() {
      if (!this.user?.stores?.length) return;
      this.loadingPriceLevels = true;
      const allStores = this.$store.getters["general/getStores"];
      await Promise.all(
        this.user.stores.map(s => this.fetchPriceLevels(s, allStores))
      );
      this.loadingPriceLevels = false;
      this.loadSellerVerification();
    },
    async loadSellerVerification() {
      if (!this.user?.email) return;
      const activeStore = this.$store.getters["general/getActiveStore"];
      const storeCode = activeStore?.code;
      if (!storeCode) return;
      this.$set(this.sellersLoadingByStore, storeCode, true);
      const { data, error } = await getInfoautoSellersByEmail(this.user.email);
      this.$set(this.sellersByStore, storeCode, data);
      this.$set(this.sellersErrorByStore, storeCode, error);
      this.$set(this.sellersLoadingByStore, storeCode, false);
    },
    async toggleSeller(seller) {
      try {
        await updateInfoautoSeller(seller.id, {
          enabled: !seller.enabled
        });
        this.$set(seller, "enabled", !seller.enabled);
        this.$toast.open({
          message: "Seller updated",
          type: "success",
          position: "bottom-right"
        });
      } catch (e) {
        this.$toast.open({
          message: "Error updating seller",
          type: "error",
          position: "bottom-right"
        });
      }
    },
    async fetchPriceLevels(storeCode, allStores) {
      if (this.priceLevelsByStore[storeCode]) return;
      const storeData = allStores.find(s => s.code === storeCode);
      const storeUrl = storeData?.endpoint || storeData?.domain;
      if (storeUrl) {
        const result = await getPriceLevelsForStore(storeUrl);
        this.$set(this.priceLevelsByStore, storeCode, result.data);
        this.$set(this.storeErrors, storeCode, result.error);
      } else {
        this.$set(this.storeErrors, storeCode, true);
      }
    },
    groupedPriceLevels(storeCode) {
      return (this.priceLevelsByStore[storeCode] || []).reduce(
        (groups, level) => {
          const dept = level.departamentoNombre || "Other";
          if (!groups[dept]) groups[dept] = [];
          groups[dept].push(level);
          return groups;
        },
        {}
      );
    },
    isPriceLevelSelected(storeCode, level) {
      const key = `${level.departamentoId}:${level.grupoPrecioId}`;
      return (this.user.storePriceLevels?.[storeCode] || []).includes(key);
    },
    togglePriceLevel(storeCode, level, checked) {
      const key = `${level.departamentoId}:${level.grupoPrecioId}`;
      let current = [...(this.user.storePriceLevels[storeCode] || [])];
      if (!Array.isArray(current)) current = [];
      this.$set(
        this.user.storePriceLevels,
        storeCode,
        checked
          ? [...current.filter(k => k !== key), key]
          : current.filter(k => k !== key)
      );
    },
    getSelectedPriceLevelLabels(storeCode) {
      const selected = this.user.storePriceLevels?.[storeCode] || [];
      if (!selected.length) return [];
      const levels = this.priceLevelsByStore[storeCode] || [];
      return selected.map(key => {
        const [deptId, groupId] = key.split(":");
        const level = levels.find(
          l => l.departamentoId === deptId && l.grupoPrecioId === groupId
        );
        return level ? `${level.descripcion} (${level.codigo})` : key;
      });
    },
    async getPermissions() {
      this.permissions = await getPermissions();
    },
    saveChanges() {
      if (!this.userIsEdited) {
        this.$toast.open({
          message: "No changes to save",
          type: "error",
          position: "bottom-right"
        });
        return;
      }
      this.loading = true;
      updateAdminUser(this.user.id, this.user)
        .then(() => {
          this.$toast.open({
            message: "User updated",
            type: "success",
            position: "bottom-right"
          });
          this.loadUser();
          this.editing = false;
        })
        .catch(error => {
          this.$toast.open({
            message: error.response?.data?.message || "Error updating user",
            type: "error",
            position: "bottom-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetLoginAttempts() {
      resetLoginAttemptsApi(this.user.id)
        .then(() => {
          this.$toast.open({
            message: "Account unlocked",
            type: "success",
            position: "bottom-right"
          });
          this.loadUser();
        })
        .catch(error => {
          this.$toast.open({
            message: error.response?.data?.message,
            type: "error",
            position: "bottom-right"
          });
        });
    },
    getResetPasswordLink() {
      getResetPasswordLinkApi(this.user.id)
        .then(response => {
          const link =
            window.location.origin + "/new-password/" + response.token;
          navigator.clipboard.writeText(link);
          this.$toast.open({
            message: "Link copied to clipboard",
            type: "success",
            position: "top-left"
          });
        })
        .catch(error => {
          this.$toast.open({
            message: error.response?.data?.message,
            type: "error",
            position: "top-left"
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.user-page {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 960px;
}

.up-back-link {
  align-self: flex-start;
  font-size: 0.825rem;
  font-weight: 600;
  color: #6366f1;
  text-decoration: none;
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  &:hover {
    background: #eef2ff;
  }
}

/* ── Header card ─────────────────────────────────── */
.up-header-card {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.up-avatar {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.up-header-info {
  flex: 1;
  min-width: 0;
}
.up-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem;
}
.up-email {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}
.up-header-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}

/* ── Action buttons ──────────────────────────────── */
.action-btn {
  padding: 0.4rem 0.875rem;
  border-radius: 0.375rem;
  font-size: 0.825rem;
  font-weight: 600;
  border: 1.5px solid #d1d5db;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  &--primary {
    background: #6366f1;
    border-color: #6366f1;
    color: #fff;
    &:hover {
      background: #4f46e5;
      border-color: #4f46e5;
    }
  }
  &--danger {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #dc2626;
    &:hover {
      background: #fee2e2;
    }
  }
}

/* ── Cards ───────────────────────────────────────── */
.up-body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.up-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.up-card-title {
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0 0 1rem;
  padding-bottom: 0.625rem;
  border-bottom: 1px solid #f3f4f6;
}

/* ── Field grid ──────────────────────────────────── */
.up-field-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem 1.5rem;
}
.up-field-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  margin-bottom: 0.25rem;
}
.up-field-value {
  font-size: 0.9rem;
  color: #111827;
}
.up-input {
  width: 100%;
  padding: 0.4rem 0.625rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}
.up-select {
  width: 100%;
  padding: 0.4rem 0.625rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}

/* ── Badges ──────────────────────────────────────── */
.role-badge {
  font-size: 0.72rem;
  font-weight: 600;
  background: #e0e7ff;
  color: #4338ca;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}
.status-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  &--active {
    background: #dcfce7;
    color: #15803d;
  }
  &--inactive {
    background: #f3f4f6;
    color: #6b7280;
  }
  &--locked {
    background: #fef2f2;
    color: #dc2626;
  }
}

/* ── Stores ──────────────────────────────────────── */
.store-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  background: #f3f4f6;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  &--default {
    background: #e0e7ff;
    color: #4338ca;
  }
}
.store-chip-default-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.7;
}
.stores-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.store-check-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  cursor: pointer;
}

/* ── Checkbox ────────────────────────────────────── */
.up-checkbox {
  appearance: none;
  width: 1rem;
  height: 1rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.25rem;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  &:checked {
    background: #6366f1;
    border-color: #6366f1;
    &::after {
      content: "";
      position: absolute;
      top: 1px;
      left: 4px;
      width: 4px;
      height: 8px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
  &:disabled {
    background: #f3f4f6;
    border-color: #e5e7eb;
    cursor: not-allowed;
  }
  &:disabled:checked {
    background: #a5b4fc;
    border-color: #a5b4fc;
  }
}

/* ── Price levels ────────────────────────────────── */
.price-levels-wrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.price-level-store {
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #f3f4f6;
}
.price-level-store-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.pl-chip {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
}
.pl-dept-group {
  margin-bottom: 0.5rem;
}
.pl-dept-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.25rem;
}
.pl-check-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.825rem;
  cursor: pointer;
  padding: 0.15rem 0;
}

/* ── Permissions ─────────────────────────────────── */
.permissions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.25rem;
}
.perm-group-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.5rem;
}
.perm-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.825rem;
  padding: 0.2rem 0;
  cursor: pointer;
  &--disabled {
    cursor: default;
  }
}

/* ── Seller status ───────────────────────────────── */
.seller-status-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.seller-status-row {
  display: flex;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #f3f4f6;
}
.seller-status-store {
  width: 120px;
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.seller-status-body {
  flex: 1;
  min-width: 0;
}
.seller-status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0;
}
.seller-status-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
}
.seller-status-detail {
  font-size: 0.825rem;
  color: #374151;
}

/* ── Save bar ────────────────────────────────────── */
.up-save-bar {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
</style>
